import { formatDate, t } from "@hakili/i18n";

/**
 * `round CN-B`, `CN-B.2`: **what every partner's card derives from the
 * accounts, and never types** , WF-CON-35 step 2: the status chip, the
 * account line (« active users, or an invitation pending since a date »), the
 * manager named under the administration.
 *
 * The status words are the drawing's (`organisations[].statut`), keyed by the
 * WIRE's own `identity.organisation_status` values (`09-01`: data, never an
 * identifier).
 */

/** `P0-CNW-029`'s directory row, the shape every card derives from. */
export interface PartnerAdministrator {
  readonly fullName: string;
  readonly email: string;
  readonly state: string;
  readonly invitationId: string | null;
}

export interface Partner {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly administrator?: PartnerAdministrator | null;
  /** `round CN-B`, WF-CON-35 step 2: the facts a card DERIVES its lines from. */
  readonly status?: string;
  /** `round CN-B`: who OPENED the partner. A true fact, and not the follower , see `managerName`. */
  readonly openedByName?: string | null;
  /**
   * `FX-3`, `M1`: who FOLLOWS the partner , `partnerships.partner_accounts.manager_user_id` resolved to a
   * name by the api (`DECISION ADDED A36`). `null` is the api's own answer for « nobody follows this yet »,
   * which the badge draws as its dash; `undefined` is an older answer that did not carry the fact.
   */
  readonly managerName?: string | null;
  readonly memberCount?: number;
  readonly activeCount?: number;
  readonly invitationPendingSince?: string | null;
}

const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), t(key));

export const STATUS_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["active", "console.partenaires.statutActive"],
  ["invitationEnvoyee", "console.partenaires.statutInvitationEnvoyee"],
  ["verificationsHorsLigne", "console.partenaires.statutVerifications"],
  ["suspendue", "console.partenaires.statutSuspendue"],
]);

export const STATUS_CHIP = new Map<string, string>([
  ["active", "bg-emerald-50 text-emerald-700"],
  ["invitationEnvoyee", "bg-amber-50 text-amber-700"],
  ["verificationsHorsLigne", "bg-gray-100 text-gray-600"],
  ["suspendue", "bg-rose-50 text-rose-600"],
]);

export function statusWord(status: string | undefined): string {
  const key = status === undefined ? undefined : STATUS_WORDS.get(status);
  return key === undefined ? (status ?? DASH) : t(key);
}

export function StatusChip({
  status,
  activeWord,
}: {
  readonly status: string | undefined;
  /** `PARTNERS-DONE-STATE`, `M2b`: the word for « active » where a list names what the chip reads (the accounts), not the panel. */
  readonly activeWord?: string;
}): React.JSX.Element {
  return (
    <span
      data-testid="partenaire-statut"
      className={
        "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
        (STATUS_CHIP.get(status ?? "") ?? "bg-gray-100 text-gray-600")
      }
    >
      {status === "active" && activeWord !== undefined ? activeWord : statusWord(status)}
    </span>
  );
}

/**
 * `S2-7` residual, `S2-FINDINGS-2` `M3a`: **the badge's word, from the same facts as `accountLine()`.**
 * The raw organisation status only moves by the opening door, a manual correction or a suspension, never
 * when an administrator accepts, so it lagged the account line on every partner that had signed in.
 * Suspension keeps priority (it blocks sign-in whatever the accounts say); otherwise a live account, then a
 * pending invitation, then the organisation's own word, in the order `accountLine()` reads them.
 */
export function partnerStatus(partner: Partner): string | undefined {
  if (partner.status === "suspendue") return "suspendue";
  const active = partner.activeCount ?? (partner.administrator?.state === "active" ? 1 : 0);
  if (active > 0) return "active";
  if (
    (partner.invitationPendingSince !== null && partner.invitationPendingSince !== undefined) ||
    partner.administrator?.state === "invitationPending"
  ) {
    return "invitationEnvoyee";
  }
  return partner.status;
}

/** WF-CON-35 step 2: the account line, DERIVED from the accounts table. */
export function accountLine(partner: Partner): string {
  const active = partner.activeCount ?? (partner.administrator?.state === "active" ? 1 : 0);
  if (active > 0) {
    return active === 1
      ? fill("console.partenaires.compteActifUn", { nom: partner.administrator?.fullName ?? DASH })
      : fill("console.partenaires.comptesActifs", { n: String(active) });
  }
  if (partner.invitationPendingSince !== null && partner.invitationPendingSince !== undefined) {
    return fill("console.partenaires.invitationDepuis", {
      date: formatDate(new Date(partner.invitationPendingSince)),
    });
  }
  if (partner.status === "invitationEnvoyee" || partner.administrator?.state === "invitationPending") {
    return t("console.partenaires.invitationEnvoyeeSansDate");
  }
  if (partner.status === "verificationsHorsLigne") return t("console.partenaires.aucunCompteVerifications");
  return t("console.partenaires.aucunCompte");
}

export const initialsOf = (name: string): string =>
  name
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

/** The manager, named under the administration alone (WF-CON-35: « the admin sees everything with the manager named »). */
export function ManagerBadge({
  name,
}: {
  readonly name: string | null | undefined;
}): React.JSX.Element | null {
  // `PARTNERS-DONE-STATE` (D-1019-3): a dash that names nobody says nothing and has no door , nothing is drawn.
  if (name === null || name === undefined || name === "") return null;
  return (
    <span
      title={fill("console.partenaires.suiviPar", { nom: name })}
      data-testid="partenaire-gestionnaire"
      className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700"
    >
      {initialsOf(name)}
    </span>
  );
}

export { DASH, fill };
